// QA Form

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Formik, Field, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import { countryList } from "../utils/utils";
import {
  getAllFlows,
  getFlowById,
  makeCallQA,
  makeCallV2,
} from "../services/flowService";
import {
  startProdServices,
  getProdServicesStatus,
  getProdServicesStatusFromTeleserver,
} from "../services/configService";
import {
  isVariableRequired,
  buildVariableValidations,
} from "../utils/variableValidation";

const MakeCallForm = ({ backend }) => {
  const [loading, setLoading] = useState();
  const [msg, setMsg] = useState("");
  const [flows, setFlows] = useState([]);
  // custom backend url
  const [customBackendUrl, setCustomBackendUrl] = useState("");

  useEffect(() => {
    // Below function has to be used when we want all the flows
    // in the Django Backend to appear in the make call Form
    const getFlowsFromBackend = async () => {
      const response = await getAllFlows();
      setFlows(response);
    };
    getFlowsFromBackend();
  }, []);

  //Dynamic form values

  const [selectedFlowId, setSelectedFlowId] = useState("");

  const [selectedFlow, setSelectedFlow] = useState(null);
  const [selectedFlowLoading, setSelectedFlowLoading] = useState(false);

  const handleFlowSelection = async (selected_flow_id) => {
    setSelectedFlow(null);
    setSelectedFlowLoading(true);
    setSelectedFlowId(selected_flow_id);
    try {
      const res = await getFlowById(selected_flow_id);
      if (res?.status === 200) {
        setSelectedFlow(res?.data);
        setSelectedFlowLoading(false);
      }
    } catch (err) {
      console.error(err);
    }
  };

  let filteredVariablesArr =
    selectedFlow?.variables?.variables.filter((elem) => elem.value_required) ||
    [];

  let form_variable_fields =
    filteredVariablesArr?.reduce((obj, elem) => {
      if (elem.type === "phone") {
        return {
          ...obj,
          [elem.name]: elem.default_value || "",
          [`countryCode_${elem.name}`]: "+91",
        };
      } else if (elem.type === "list") {
        // For list type, store the default value as a JSON string
        return {
          ...obj,
          [elem.name]: Array.isArray(elem.default_value)
            ? JSON.stringify(elem.default_value)
            : "[]",
        };
      } else {
        return {
          ...obj,
          [elem.name]: elem.default_value || "",
        };
      }
    }, {}) || {};

  // Yup validation schema

  const validationSchema = useMemo(() => {
    return Yup.object().shape({
      name: Yup.string()
        .min(1, "Name must be atleast 1 character")
        .matches(/^[^_]*$/, "Name cannot contain underscore")
        .required("Name is required"),
      countryCode: Yup.string().required("Country Code is required"),
      phone: Yup.string()
        .matches(/^[0-9]{10}$/, "Phone number must be 10 digit")
        .required("Phone number is required"),
      ...buildVariableValidations(
        Object.keys(form_variable_fields),
        filteredVariablesArr
      ),
    });
    // Key on filteredVariablesArr (not form_variable_fields): it carries the
    // names, types, defaults AND is_optional flags the schema depends on, so
    // switching flows always rebuilds the required/optional rules.
  }, [JSON.stringify(filteredVariablesArr)]);

  // Start Prod Services
  // Derive server_version of the selected flow ("v2" for agentic flows, else V1).
  const selectedFlowServerVersion = useMemo(() => {
    const f = Array.isArray(flows)
      ? flows.find((x) => x.id === selectedFlowId)
      : null;
    return f?.server_version === "v2" ? "v2" : "";
  }, [flows, selectedFlowId]);

  const [servicesStartTime, setServicesStartTime] = useState("");
  const [startProdServicesLoading, setStartProdServicesLoading] =
    useState(false);
  const [prodServicesStatus, setProdServicesStatus] = useState({});
  const [prodServicesStatusLoading, setProdServicesStatusLoading] =
    useState(false);

  const handleGetProdServicesStatus = useCallback(async () => {
    if (!selectedFlowId) return;
    setProdServicesStatusLoading(true);
    try {
      const res = await getProdServicesStatus(
        selectedFlowId,
        selectedFlowServerVersion
      );
      const res2 = await getProdServicesStatusFromTeleserver(
        selectedFlowId,
        selectedFlowServerVersion
      );
      if (res?.data?.status === "success" && res2?.status === 200) {
        setProdServicesStatus({
          ...res?.data?.message,
          services_status: res2?.data?.status,
          res2_message: res2?.data?.message,
        });
      }
    } catch (err) {
      console.error("Error occurred while getting prod services status", err);
    }
    setProdServicesStatusLoading(false);
  }, [selectedFlowId, selectedFlowServerVersion]);

  const handleStartProdServicesClick = async () => {
    setStartProdServicesLoading(true);
    try {
      await startProdServices(selectedFlowId, selectedFlowServerVersion);
      await handleGetProdServicesStatus();
      const startTime = new Date().toLocaleTimeString();
      localStorage.setItem(`${selectedFlowId}_start_time`, startTime);
      setServicesStartTime(startTime);
    } catch (err) {
      console.error("Error occurred while starting prod services", err);
    }
    setStartProdServicesLoading(false);
  };

  // Load saved start time + refresh status whenever the selected flow changes.
  useEffect(() => {
    if (!selectedFlowId) return;
    const savedStartTime = localStorage.getItem(`${selectedFlowId}_start_time`);
    setServicesStartTime(savedStartTime || "");
    handleGetProdServicesStatus();
  }, [selectedFlowId, handleGetProdServicesStatus]);

  return (
    <>
      <div className="my-4 text-base list-none text-black bg-white divide-y divide-gray-100 rounded-md shadow-lg md:w-[30rem] text-left p-4 border border-slate-300">
        <div className="space-y-4">
          {flows.length !== 0 ? (
            <div>
              <label
                htmlFor="flow"
                className="block text-sm font-medium text-gray-700 break-words">
                <span className="text-red-500">*</span>Flow
              </label>
              <select
                name="flow"
                id="flow"
                onChange={(e) => {
                  handleFlowSelection(e.target.value);
                }}
                value={selectedFlowId}
                className={
                  "mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md cursor-pointer"
                }>
                <option value="" disabled>
                  Select FLow
                </option>
                {Array.isArray(flows) ? (
                  flows.map((flow, index) => (
                    <option key={flow.id} value={flow.id}>
                      {flow.name}
                    </option>
                  ))
                ) : (
                  <span>No flows available for this url</span>
                )}
              </select>
            </div>
          ) : (
            <div className="text-sm text-center">Loading all flows...</div>
          )}
          {selectedFlowLoading && (
            <div className="text-sm text-center">Loading Flow Variables...</div>
          )}
          {selectedFlowId && selectedFlow && (
            <>
              <div className="border border-gray-300 flex flex-col gap-2 p-3 rounded bg-gray-100">
                <div className="flex gap-2 items-center">
                  <div className="text-nowrap border-r border-gray-400 pr-4">
                    <button
                      type="button"
                      disabled={startProdServicesLoading}
                      onClick={handleStartProdServicesClick}
                      className="bg-gray-800 hover:bg-gray-700 disabled:bg-gray-300 text-gray-100 px-3 py-2 rounded cursor-pointer whitespace-nowrap text-sm font-medium">
                      {startProdServicesLoading
                        ? "Starting..."
                        : "Start Prod Services"}
                    </button>
                  </div>

                  <div className="flex flex-col pl-2 gap-1 w-full">
                    <div className="text-sm flex items-center gap-1 w-full text-gray-700">
                      <div>
                        Status:{" "}
                        {prodServicesStatusLoading ? (
                          <span>Loading...</span>
                        ) : prodServicesStatus?.schedule_status ===
                          "Not Found" ? (
                          <span>Down</span>
                        ) : prodServicesStatus?.schedule_status === "Found" &&
                          prodServicesStatus?.services_status ? (
                          <span>Up</span>
                        ) : (
                          <span>Starting...</span>
                        )}
                      </div>

                      <button
                        type="button"
                        disabled={prodServicesStatusLoading}
                        onClick={handleGetProdServicesStatus}
                        title="Refresh status"
                        className="ml-auto bg-gray-300 rounded hover:bg-gray-400 disabled:bg-gray-300 cursor-pointer flex items-center justify-center h-5 w-5">
                        {prodServicesStatusLoading ? (
                          "..."
                        ) : (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            className="w-3.5 h-3.5">
                            <path
                              fillRule="evenodd"
                              d="M4.755 10.059a7.5 7.5 0 0112.548-3.364l1.903 1.903h-3.183a.75.75 0 100 1.5h4.992a.75.75 0 00.75-.75V4.356a.75.75 0 00-1.5 0v3.18l-1.9-1.9A9 9 0 003.306 9.67a.75.75 0 101.45.388zm15.408 3.352a.75.75 0 00-.919.53 7.5 7.5 0 01-12.548 3.364l-1.902-1.903h3.183a.75.75 0 000-1.5H2.984a.75.75 0 00-.75.75v4.992a.75.75 0 001.5 0v-3.18l1.9 1.9a9 9 0 0015.059-4.035.75.75 0 00-.53-.918z"
                              clipRule="evenodd"
                            />
                          </svg>
                        )}
                      </button>
                    </div>
                    {prodServicesStatusLoading ? (
                      <div className="h-[2.25rem]"></div>
                    ) : prodServicesStatus?.schedule_status === "Not Found" ? (
                      <>
                        <div className="mr-1 text-xs text-gray-600">
                          Services start in: NA
                        </div>
                        <div className="mr-1 text-xs text-gray-600">
                          Services stop in: NA
                        </div>
                      </>
                    ) : (
                      prodServicesStatus?.schedule_status === "Found" && (
                        <>
                          <div className="mr-1 text-xs text-gray-600">
                            {prodServicesStatus?.services_status ? (
                              <span>Services are up</span>
                            ) : prodServicesStatus?.time_left_to_start_services !==
                              "Services are up" ? (
                              `Services start in: ${prodServicesStatus?.time_left_to_start_services} (approx)`
                            ) : (
                              `Taking longer than usual. Services will start in some time`
                            )}
                          </div>
                          <div className="mr-1 text-xs text-gray-600">
                            {`Services stop in: ${prodServicesStatus?.time_left_to_stop_prod}`}
                          </div>
                        </>
                      )
                    )}
                  </div>
                </div>
                <div className="text-sm text-gray-600">
                  {servicesStartTime
                    ? `After starting initially, please wait 15 minutes for services to start. Please click on the button again within 45 minutes post that, if you wish to continue using prod services.`
                    : ""}
                </div>
              </div>

              {!!!backend && (
                <div>
                  <label
                    htmlFor="custom_backend"
                    className="block text-sm font-medium text-gray-700">
                    Custom Backend
                  </label>
                  <input
                    value={customBackendUrl}
                    onChange={(e) => {
                      setCustomBackendUrl(e.target.value);
                    }}
                    name="custom_backend"
                    type="text"
                    className={
                      "mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                    }
                  />
                </div>
              )}

              <Formik
                initialValues={{
                  name: "Kanik",
                  countryCode: "+91",
                  phone: "",
                  ...form_variable_fields,
                }}
                enableReinitialize
                validationSchema={validationSchema}
                onSubmit={(values) => {
                  setMsg("");
                  setLoading(true);

                  // V2 / agentic flows use the V2 teleserver call API with a
                  // flat variable_values map (required vars from the form,
                  // others from their defaults).
                  if (selectedFlowServerVersion === "v2") {
                    // List variables must be sent as arrays, not JSON strings.
                    const toListValue = (raw) => {
                      if (Array.isArray(raw)) return raw;
                      try {
                        const parsed = JSON.parse(raw);
                        return Array.isArray(parsed) ? parsed : [];
                      } catch (e) {
                        return [];
                      }
                    };

                    const variable_values = (
                      selectedFlow?.variables?.variables || []
                    ).reduce((acc, v) => {
                      if (v.type === "list") {
                        acc[v.name] = toListValue(
                          v.value_required ? values[v.name] : v.default_value
                        );
                      } else if (v.value_required) {
                        acc[v.name] =
                          v.type === "phone"
                            ? `${values[`countryCode_${v.name}`] || ""}${
                                values[v.name] || ""
                              }`
                            : values[v.name] ?? (v.default_value || "");
                      } else {
                        acc[v.name] = v.default_value || "";
                      }
                      return acc;
                    }, {});

                    makeCallV2({
                      agent_id: selectedFlowId,
                      phone_number: values.countryCode + values.phone,
                      variable_values,
                      custom_backend_url: customBackendUrl,
                    }).then((response) => {
                      setLoading(false);
                      const d = response?.data;
                      if (d?.call_id || d?.status) {
                        setMsg(`Call ${d.status || "started"}`);
                      } else {
                        setMsg(d?.message || d?.detail || "some error occurred");
                      }
                    });
                    return;
                  }

                  // Calling QA Backend

                  const data = Object.keys(form_variable_fields).reduce(
                    (obj, elem) => {
                      if (!elem) {
                        return obj;
                      }
                      const other_variable = filteredVariablesArr.find(
                        (item) => item.name === elem
                      );
                      if (!other_variable) {
                        return obj;
                      }
                      if (other_variable.type === "phone") {
                        return {
                          ...obj,
                          other_values: {
                            ...obj.other_values,
                            [elem]: `${values[`countryCode_${elem}`]}${
                              values[elem]
                            }`,
                          },
                        };
                      } else {
                        return {
                          ...obj,
                          other_values: {
                            ...obj.other_values,
                            [elem]: values[elem],
                          },
                        };
                      }
                    },
                    {
                      user_name: values.name,
                      user_phone: values.countryCode + values.phone,
                      flow: selectedFlow.backend_json,
                      call_flow_id: selectedFlowId,
                      other_values: {},
                      contact_id: "dummy_qa",
                      campaign_id: "dummy_qa",
                      round_id: "dummy_qa",
                    }
                  );
                  makeCallQA(data, backend, customBackendUrl)
                    .then((response) => {
                      setLoading(false);
                      setMsg(response.data?.message);
                    })
                    .catch((err) => {
                      setLoading(false);
                      setMsg("some error occurred");
                    });
                }}>
                {({ errors, touched, values, handleChange, handleBlur }) => {
                  return (
                    <Form className="space-y-4">
                      <div>
                        <label
                          htmlFor="name"
                          className="block text-sm font-medium text-gray-700">
                          <span className="text-red-500">*</span>Name
                        </label>
                        <Field
                          name="name"
                          type="text"
                          className={
                            "mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" +
                            (errors.name && touched.name
                              ? " border-red-500"
                              : "")
                          }
                        />
                        <ErrorMessage
                          name="name"
                          component="div"
                          className="text-red-500 text-sm mt-1"
                        />
                      </div>
                      <div className="flex w-full gap-3">
                        <div className="w-[50%]">
                          <label
                            htmlFor="countryCode"
                            className="block text-sm font-medium text-gray-700 break-words">
                            <span className="text-red-500">*</span>Phone
                          </label>
                          <Field
                            as="select"
                            name="countryCode"
                            className={
                              "mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" +
                              (errors.countryCode && touched.countryCode
                                ? " border-red-500"
                                : "")
                            }>
                            {countryList.map((country, index) => (
                              <option
                                key={country.code}
                                value={country.dial_code}>
                                {`${country.flag} (${country.dial_code})      ${country.code} ${country.name}`}
                              </option>
                            ))}
                          </Field>
                          <ErrorMessage
                            name="countryCode"
                            component="div"
                            className="text-red-500 text-sm mt-1"
                          />
                        </div>

                        <div className="w-full relative top-3 flex flex-col gap-2">
                          <label
                            htmlFor="phone"
                            className="block text-sm font-medium text-gray-700 break-words"></label>
                          <div className="gap-3">
                            <Field
                              name="phone"
                              type="number"
                              className={
                                "mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" +
                                (errors.phone && touched.phone
                                  ? " border-red-500"
                                  : "")
                              }
                            />
                            <ErrorMessage
                              name="phone"
                              component="div"
                              className="text-red-500 text-sm mt-1"
                            />
                          </div>
                        </div>
                      </div>

                      {filteredVariablesArr.map((v) => {
                        if (v.type === "string") {
                          return (
                            <div key={v.name}>
                              <label
                                htmlFor={v.name}
                                className="block text-sm font-medium text-gray-700 break-words">
                                {isVariableRequired(v) && (
                                  <span className="text-red-500">*</span>
                                )}
                                {v.name}
                              </label>
                              <Field
                                name={v.name}
                                type="text"
                                className={
                                  "mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" +
                                  (errors[v.name] && touched[v.name]
                                    ? " border-red-500"
                                    : "")
                                }
                              />
                              <ErrorMessage
                                name={v.name}
                                component="div"
                                className="text-red-500 text-sm mt-1"
                              />
                            </div>
                          );
                        } else if (v.type === "dropdown") {
                          return (
                            <div key={v.name}>
                              <label
                                htmlFor={v.name}
                                className="block text-sm font-medium text-gray-700 break-words">
                                {isVariableRequired(v) && (
                                  <span className="text-red-500">*</span>
                                )}
                                {v.name}
                              </label>
                              <Field
                                className={
                                  "mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" +
                                  (errors[v.name] && touched[v.name]
                                    ? " border-red-500"
                                    : "")
                                }
                                as="select"
                                name={v.name}>
                                <option value="">Select {v.name}</option>
                                {v.options.map((option) => (
                                  <option key={option} value={option}>
                                    {option}
                                  </option>
                                ))}
                              </Field>
                              <ErrorMessage
                                name={v.name}
                                component="div"
                                className="text-red-500 text-sm mt-1"
                              />
                            </div>
                          );
                        } else if (v.type === "phone") {
                          return (
                            <div key={v.name} className="flex w-full gap-3">
                              <div className="w-[50%]">
                                <label
                                  htmlFor={`countryCode_${v.name}`}
                                  className="block text-sm font-medium text-gray-700 break-words">
                                  {isVariableRequired(v) && (
                                    <span className="text-red-500">*</span>
                                  )}
                                  {v.name}
                                </label>
                                <Field
                                  as="select"
                                  name={`countryCode_${v.name}`}
                                  className={
                                    "mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" +
                                    (errors[`countryCode_${v.name}`] &&
                                    touched[`countryCode_${v.name}`]
                                      ? " border-red-500"
                                      : "")
                                  }>
                                  {countryList.map((country, index) => (
                                    <option
                                      key={country.code}
                                      value={country.dial_code}>
                                      {`${country.flag} (${country.dial_code})      ${country.code} ${country.name}`}
                                    </option>
                                  ))}
                                </Field>
                                <ErrorMessage
                                  name={`countryCode_${v.name}`}
                                  component="div"
                                  className="text-red-500 text-sm mt-1"
                                />
                              </div>

                              <div className="w-full relative top-3 flex flex-col gap-2">
                                <label
                                  htmlFor={v.name}
                                  className="block text-sm font-medium text-gray-700 break-words"></label>
                                <div className="gap-3">
                                  <Field
                                    name={v.name}
                                    type="number"
                                    className={
                                      "mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" +
                                      (errors[v.name] && touched[v.name]
                                        ? " border-red-500"
                                        : "")
                                    }
                                  />
                                  <ErrorMessage
                                    name={v.name}
                                    component="div"
                                    className="text-red-500 text-sm mt-1"
                                  />
                                </div>
                              </div>
                            </div>
                          );
                        } else if (v.type === "textarea") {
                          return (
                            <div key={v.name}>
                              <label
                                htmlFor={v.name}
                                className="block text-sm font-medium text-gray-700 break-words">
                                {isVariableRequired(v) && (
                                  <span className="text-red-500">*</span>
                                )}
                                {v.name}
                              </label>
                              <Field
                                as="textarea"
                                name={v.name}
                                rows={4} // Set the desired number of rows
                                className={
                                  "mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" +
                                  (errors[v.name] && touched[v.name]
                                    ? " border-red-500"
                                    : "")
                                }
                              />
                              <ErrorMessage
                                name={v.name}
                                component="div"
                                className="text-red-500 text-sm mt-1"
                              />
                            </div>
                          );
                        } else if (v.type === "list") {
                          return (
                            <div key={v.name}>
                              <label
                                htmlFor={v.name}
                                className="block text-sm font-medium text-gray-700 break-words">
                                {isVariableRequired(v) && (
                                  <span className="text-red-500">*</span>
                                )}
                                {v.name} (JSON Array)
                              </label>
                              <Field
                                name={v.name}
                                as="textarea"
                                rows={4}
                                className={
                                  "mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md font-mono" +
                                  (errors[v.name] && touched[v.name]
                                    ? " border-red-500"
                                    : "")
                                }
                              />
                              <div className="text-xs text-gray-500 mt-1">
                                Enter a valid JSON array, e.g., ["item1",
                                "item2", "item3"] or [1,2,3]
                              </div>
                              <ErrorMessage
                                name={v.name}
                                component="div"
                                className="text-red-500 text-sm mt-1"
                              />
                            </div>
                          );
                        }
                      })}

                      <div className="flex flex-col items-center gap-2 justify-center z-30">
                        <button
                          type="submit"
                          className={`border-[1px] rounded text-white px-5 py-[6px]  ${
                            loading
                              ? "bg-gray-300 text-slate-600"
                              : "bg-black border-black"
                          }`}
                          disabled={loading}>
                          Submit
                        </button>
                        <div>{msg}</div>
                      </div>
                    </Form>
                  );
                }}
              </Formik>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default MakeCallForm;
